import React, { useEffect, useMemo, useState } from 'react';
import { store } from 'react-notifications-component';
import DateRange from '../date-range';
import * as S from './periodo-selector-styles';

// Limite máximo de intervalo entre data inicial/final aceito na tela de
// Relatórios - espelha PERIODO_MAX_MESES do backend (periodo.dto.ts), fonte
// da verdade; aqui é só validação de UX pra não deixar o usuário disparar
// uma requisição que o backend vai rejeitar de qualquer forma.
const PERIODO_MAX_MESES = 24;

const addMonths = (dateStr, months) => {
    const date = new Date(dateStr);
    date.setMonth(date.getMonth() + months);
    return date;
};

// Seletor de período global do Header dos Relatórios - embrulha o
// DateRange já existente (src/components/date-range) com a validação de
// intervalo (final ≥ inicial, no máximo 24 meses) e de campo vazio: só
// avisa o componente pai (via onChange) quando os dois campos estão
// preenchidos e o intervalo é válido - antes disso mantém a última seleção
// válida como estava, com o erro sinalizado inline.
const PeriodoSelector = ({ dtInicial, dtFinal, onChange }) => {
    const [pendingInicial, setPendingInicial] = useState(dtInicial);
    const [pendingFinal, setPendingFinal] = useState(dtFinal);

    // Mantém sincronizado se o período mudar por fora (ex.: veio da URL no
    // primeiro load, ou foi resetado em outro lugar).
    useEffect(() => {
        setPendingInicial(dtInicial);
        setPendingFinal(dtFinal);
    }, [dtInicial, dtFinal]);

    const erro = useMemo(() => {
        if (!pendingInicial || !pendingFinal) return false; // incompleto ainda não é "erro" pro usuário
        if (new Date(pendingFinal) < new Date(pendingInicial)) return true;
        if (new Date(pendingFinal) > addMonths(pendingInicial, PERIODO_MAX_MESES)) return true;
        return false;
    }, [pendingInicial, pendingFinal]);

    const errorMessage = useMemo(() => {
        if (!erro) return '';
        if (new Date(pendingFinal) < new Date(pendingInicial)) {
            return 'Data final deve ser posterior à inicial.';
        }
        return `Intervalo máximo de ${PERIODO_MAX_MESES} meses.`;
    }, [erro, pendingInicial, pendingFinal]);

    useEffect(() => {
        if (!pendingInicial || !pendingFinal) return;
        if (erro) return;
        if (pendingInicial === dtInicial && pendingFinal === dtFinal) return;
        onChange(pendingInicial, pendingFinal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingInicial, pendingFinal, erro]);

    // Erro de período agora vira notificação (igual ao resto da tela), em
    // vez de texto fixo embaixo do input.
    useEffect(() => {
        if (!erro) return;

        store.addNotification({
            title: "Erro!",
            message: errorMessage,
            type: "danger",
            insert: "top",
            container: "top-right",
            animationIn: ["animated", "fadeIn"],
            animationOut: ["animated", "fadeOut"],
            dismiss: {
                duration: 4000,
                onScreen: true
            }
        });
    }, [erro, errorMessage]);

    return (
        <S.Wrapper>
            <DateRange
                initial={pendingInicial}
                final={pendingFinal}
                onChangeInitial={setPendingInicial}
                onChangeFinal={setPendingFinal}
                erro={erro}
                marginBottom="0"
                compact
            />
        </S.Wrapper>
    );
};

export default PeriodoSelector;
